import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { createTenant, getTenant, updateTenant, Tenant } from '../services/tenantService';
import { Database, CheckCircle2, MapPin, Sparkles, Link as LinkIcon, Building2, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'Virgin Islands' }
];

interface Props {
  tenantId?: string;
}

export const OnboardingForm: React.FC<Props> = ({ tenantId }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [stepDirection, setStepDirection] = useState<'forward' | 'backward'>('forward');
  const [stepError, setStepError] = useState<string>('');
  
  const [formData, setFormData] = useState<Partial<Tenant>>({
    name: '',
    ein: '',
    merchantId: '',
    contactEmail: '',
    contactPhone: '',
    contactName: '',
    businessType: '',
    locationName: '',
    dbaName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [businessId, setBusinessId] = useState<string | null>(tenantId || null);
  const [loading, setLoading] = useState(!!tenantId);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (auth.currentUser?.email && !formData.contactEmail) {
      setFormData(prev => ({ ...prev, contactEmail: auth.currentUser!.email! }));
    }
    if (tenantId) {
      const fetchData = async () => {
        const data = await getTenant(tenantId);
        if (data) {
          setFormData(data);
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [tenantId]);

  const validateStep1 = () => {
    if (!formData.name?.trim()) return 'Legal Entity Name is required';
    if (!formData.dbaName?.trim()) return 'Operating Name (DBA) is required';
    if (!formData.locationName?.trim()) return 'Location Name is required';
    if (!formData.contactName?.trim()) return 'Primary Contact Name is required';
    if (!formData.contactEmail?.trim()) return 'Contact Email is required';
    if (!formData.businessType?.trim()) return 'Please select an Industry / Category';
    if (!tenantId && !formData.ein?.trim()) return 'Tax ID / EIN is required';
    if (!tenantId && !formData.merchantId?.trim()) return 'POS Merchant ID is required';
    return '';
  };

  const handleNextStep = (e: React.MouseEvent) => {
    e.preventDefault();
    const error = validateStep1();
    if (error) {
      setStepError(error);
      return;
    }
    setStepError('');
    setStepDirection('forward');
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevStep = () => {
    setStepError('');
    setStepDirection('backward');
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('');
    setStepError('');
    
    // Validate required step 2 fields
    if (!formData.addressLine1?.trim()) {
      setStepError('Street Address is required');
      return;
    }
    if (!formData.city?.trim()) {
      setStepError('City is required');
      return;
    }
    if (!formData.state?.trim()) {
      setStepError('Please select a State Jurisdiction');
      return;
    }
    if (!formData.zipCode?.trim()) {
      setStepError('ZIP Code is required');
      return;
    }

    try {
      if (tenantId) {
        await updateTenant(tenantId, formData);
        setStatus('Settings updated successfully!');
        setTimeout(() => setStatus(''), 3000);
      } else {
        const pendingRef = sessionStorage.getItem('pendingReferral');
        const tenantPayload: any = {
          ...formData,
        };
        // Remove any undefined values to prevent Firestore errors
        Object.keys(tenantPayload).forEach(key => tenantPayload[key] === undefined && delete tenantPayload[key]);

        if (pendingRef) {
          tenantPayload.referredBy = pendingRef;
        }
        const id = await createTenant(tenantPayload as Omit<Tenant, 'tenantId' | 'status' | 'createdAt'>);
        if (pendingRef) {
          sessionStorage.removeItem('pendingReferral');
        }
        if (auth.currentUser) {
          console.log('Attempting to create user doc for:', auth.currentUser.uid, 'with tenantId:', id);
          await setDoc(doc(db, 'users', auth.currentUser.uid), {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
            tenantId: id,
            role: 'admin'
          });
          console.log('Successfully created user doc');
        }
        setBusinessId(id);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus(`Error: ${(error as Error).message}`);
    }
  };

  if (loading) return <div>Loading...</div>;

  if (businessId && !tenantId) {
    return (
      <div className="p-6 bg-white shadow-xl rounded-2xl max-w-lg mx-auto border border-gray-100 text-center space-y-4 my-8">
        <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 size={32} />
        </div>
        <h2 className="text-2xl font-black text-gray-900 tracking-tight">You're all set, {formData.contactName}! 🎉</h2>
        <p className="text-sm text-gray-600">Your TipValue AI workspace is ready with your 30-day free Enhanced trial.</p>
        <div className="p-3 bg-gray-50 rounded-xl font-mono text-xs text-gray-700 border border-gray-100">
          <strong>Business ID:</strong> {businessId}
        </div>
        <button 
          onClick={() => window.location.href = '/'}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3.5 px-4 font-bold text-sm shadow-lg shadow-blue-500/20 transition-colors"
        >
          Continue to Dashboard &rarr;
        </button>
      </div>
    );
  }

  // Animation variants for smooth step slides
  const slideVariants = {
    initial: (dir: 'forward' | 'backward') => ({
      opacity: 0,
      x: dir === 'forward' ? 24 : -24,
    }),
    animate: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.25, ease: 'easeOut' }
    },
    exit: (dir: 'forward' | 'backward') => ({
      opacity: 0,
      x: dir === 'forward' ? -24 : 24,
      transition: { duration: 0.2, ease: 'easeIn' }
    })
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-3xl mx-auto py-3 px-3 sm:px-4">
      {/* Header Banner */}
      <div className="bg-white p-5 sm:p-6 rounded-2xl border border-gray-100 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight uppercase font-sans">
              {tenantId ? 'Business Profile' : 'Set Up Your Business Profile'}
            </h2>
            <p className="text-gray-500 mt-1 text-xs sm:text-sm leading-relaxed">
              {tenantId 
                ? 'Manage your legal business identity, operating location, and physical address.' 
                : 'Complete 2 quick steps to activate your automated tip pooling and compliance tools.'}
            </p>
          </div>
          {!tenantId && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-black uppercase tracking-wider shrink-0 self-start sm:self-auto">
              <Sparkles className="w-3.5 h-3.5" />
              <span>30-Day Free Trial</span>
            </div>
          )}
        </div>
      </div>

      {tenantId && (
        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
          <Database size={20} className="text-gray-400" />
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Business ID</span>
            <span className="text-sm font-mono font-bold text-gray-700">{tenantId}</span>
          </div>
        </div>
      )}

      {/* Progress Wizard Tabs (Shown during new onboarding) */}
      {!tenantId && (
        <div className="bg-white p-3.5 rounded-2xl border border-gray-100 shadow-xs">
          <div className="flex items-center justify-between gap-2 mb-2">
            <button
              type="button"
              onClick={() => {
                if (currentStep === 2) handlePrevStep();
              }}
              className={`flex-1 flex items-center gap-2.5 p-2 rounded-xl text-left transition-all ${
                currentStep === 1 
                  ? 'bg-blue-50 text-blue-900 font-bold border border-blue-200/80 shadow-xs' 
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                currentStep > 1 
                  ? 'bg-emerald-500 text-white' 
                  : currentStep === 1 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep > 1 ? '✓' : '1'}
              </div>
              <div className="truncate">
                <div className="text-[9px] font-black uppercase tracking-wider text-gray-400">Step 1</div>
                <div className="text-xs font-bold truncate">Business Identity</div>
              </div>
            </button>

            <ArrowRight size={14} className="text-gray-300 shrink-0" />

            <button
              type="button"
              onClick={(e) => {
                if (currentStep === 1) handleNextStep(e);
              }}
              className={`flex-1 flex items-center gap-2.5 p-2 rounded-xl text-left transition-all ${
                currentStep === 2 
                  ? 'bg-blue-50 text-blue-900 font-bold border border-blue-200/80 shadow-xs' 
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                currentStep === 2 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
              <div className="truncate">
                <div className="text-[9px] font-black uppercase tracking-wider text-gray-400">Step 2</div>
                <div className="text-xs font-bold truncate">Location & Jurisdiction</div>
              </div>
            </button>
          </div>

          {/* Linear Progress Bar */}
          <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
            <div 
              className="bg-blue-600 h-full transition-all duration-300 ease-out rounded-full"
              style={{ width: currentStep === 1 ? '50%' : '100%' }}
            />
          </div>
        </div>
      )}

      {/* Validation Alert */}
      {stepError && (
        <div className="p-3.5 bg-red-50 text-red-700 border border-red-200 rounded-2xl text-xs font-bold flex items-center gap-2 animate-in fade-in">
          <span>⚠️</span>
          <span>{stepError}</span>
        </div>
      )}

      {/* Animated Step Content (Wizard for Onboarding, Flat for Settings) */}
      {!tenantId ? (
        <AnimatePresence mode="wait" custom={stepDirection}>
          {currentStep === 1 ? (
            <motion.div
              key="step1"
              custom={stepDirection}
              variants={slideVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-4"
            >
              <div className="bg-white p-5 sm:p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                  <Building2 size={18} className="text-blue-600" />
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">Business Identity & Contact</h3>
                    <p className="text-[11px] text-gray-400">Step 1 of 2: Define your establishment and contact profile</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Legal Entity Name *</label>
                    <input 
                      name="name" 
                      placeholder="e.g., Acme Hospitality LLC" 
                      value={formData.name || ''} 
                      onChange={e => {
                        setFormData({...formData, name: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Operating Name (DBA) *</label>
                    <input 
                      name="dbaName" 
                      placeholder="e.g., The Rustic Bistro" 
                      value={formData.dbaName || ''} 
                      onChange={e => {
                        setFormData({...formData, dbaName: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Location / Branch Name *</label>
                    <input 
                      name="locationName" 
                      placeholder="e.g., Downtown Flagship" 
                      value={formData.locationName || ''} 
                      onChange={e => {
                        setFormData({...formData, locationName: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Primary Contact Name *</label>
                    <input 
                      name="contactName" 
                      placeholder="Your Full Name" 
                      value={formData.contactName || ''} 
                      onChange={e => {
                        setFormData({...formData, contactName: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Contact Email Address *</label>
                    <input 
                      name="contactEmail" 
                      type="email" 
                      placeholder="admin@example.com" 
                      value={formData.contactEmail || ''} 
                      onChange={e => {
                        setFormData({...formData, contactEmail: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Industry / Category *</label>
                    <select 
                      name="businessType" 
                      value={formData.businessType || ''} 
                      onChange={e => {
                        setFormData({...formData, businessType: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field font-medium" 
                      required
                    >
                      <option value="">Select Industry</option>
                      <option>Restaurant</option>
                      <option>Bar</option>
                      <option>Salon / Spa</option>
                      <option>Bowling / Entertainment</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Tax ID / EIN *</label>
                    <input 
                      name="ein" 
                      placeholder="IRS EIN (e.g., 12-3456789)" 
                      value={formData.ein || ''} 
                      onChange={e => {
                        setFormData({...formData, ein: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field font-mono" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">POS Merchant ID *</label>
                    <input 
                      name="merchantId" 
                      placeholder="POS Terminal / Merchant ID" 
                      value={formData.merchantId || ''} 
                      onChange={e => {
                        setFormData({...formData, merchantId: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field font-mono" 
                      required 
                    />
                  </div>
                </div>
              </div>

              {/* Step 1 CTA */}
              <button
                type="button"
                onClick={handleNextStep}
                className="btn-primary w-full py-4 text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-blue-500/20 flex items-center justify-center gap-2 min-h-[48px]"
              >
                <span>Continue to Location (Step 2)</span>
                <ArrowRight size={16} />
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              custom={stepDirection}
              variants={slideVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-4"
            >
              <div className="bg-white p-5 sm:p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
                  <MapPin size={18} className="text-blue-600" />
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">Physical Location & State Jurisdiction</h3>
                    <p className="text-[11px] text-gray-400">Step 2 of 2: Required for state tip law and compliance enforcement</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1 sm:col-span-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Street Address *</label>
                    <input 
                      name="addressLine1" 
                      placeholder="123 Main Street" 
                      value={formData.addressLine1 || ''} 
                      onChange={e => {
                        setFormData({...formData, addressLine1: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Suite, Floor, Building (Optional)</label>
                    <input 
                      name="addressLine2" 
                      placeholder="Suite 400" 
                      value={formData.addressLine2 || ''} 
                      onChange={e => setFormData({...formData, addressLine2: e.target.value})} 
                      className="input-field" 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">City *</label>
                    <input 
                      name="city" 
                      placeholder="City" 
                      value={formData.city || ''} 
                      onChange={e => {
                        setFormData({...formData, city: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">State / Jurisdiction *</label>
                    <select 
                      name="state" 
                      value={formData.state || ''} 
                      onChange={e => {
                        setFormData({...formData, state: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field font-medium text-gray-900" 
                      required
                    >
                      <option value="">Select State Jurisdiction</option>
                      {US_STATES.map(st => (
                        <option key={st.code} value={st.code}>
                          {st.name} ({st.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">ZIP / Postal Code *</label>
                    <input 
                      name="zipCode" 
                      placeholder="90210" 
                      value={formData.zipCode || ''} 
                      onChange={e => {
                        setFormData({...formData, zipCode: e.target.value});
                        if (stepError) setStepError('');
                      }} 
                      className="input-field" 
                      required 
                    />
                  </div>
                </div>

                {formData.state === 'CA' && (
                  <div className="p-3.5 bg-blue-50/80 rounded-xl border border-blue-200/80 text-blue-900 text-xs flex gap-2.5 items-start mt-2">
                    <span className="text-base leading-none">⚖️</span>
                    <div>
                      <span className="font-bold">California Jurisdiction Detected:</span> Under California Labor Code § 351, credit card merchant processing fee deductions on employee tips are prohibited. System policies will automatically enforce full tip pass-through.
                    </div>
                  </div>
                )}
                
                {formData.state && formData.state !== 'CA' && (
                  <div className="p-3 bg-emerald-50/80 rounded-xl border border-emerald-200/80 text-emerald-900 text-xs flex gap-2 items-center mt-2">
                    <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
                    <span>State rules configured. Automated compliance checks will apply to your tip calculations.</span>
                  </div>
                )}
              </div>

              {/* Navigation Action Buttons for Step 2 */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="w-full sm:w-1/3 py-3.5 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors min-h-[48px]"
                >
                  <ArrowLeft size={16} />
                  <span>Back to Step 1</span>
                </button>

                <button 
                  type="submit" 
                  className="w-full sm:w-2/3 btn-primary py-4 text-xs font-black uppercase tracking-[0.15em] shadow-xl shadow-blue-500/20 min-h-[48px] flex items-center justify-center gap-2"
                >
                  <span>Complete Setup & Start 30-Day Trial</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        /* Unified View when editing settings inside App Settings Tab */
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest border-b border-gray-100 pb-2">Business Identity</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Legal Entity Name</label>
                <input name="name" placeholder="Business Name" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Operating Name (DBA)</label>
                <input name="dbaName" placeholder="Operating Name (DBA)" value={formData.dbaName || ''} onChange={e => setFormData({...formData, dbaName: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Location Name</label>
                <input name="locationName" placeholder="Location Name / Branch" value={formData.locationName || ''} onChange={e => setFormData({...formData, locationName: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Primary Contact</label>
                <input name="contactName" placeholder="Your Name" value={formData.contactName || ''} onChange={e => setFormData({...formData, contactName: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Email Address</label>
                <input name="contactEmail" type="email" placeholder="Your Email" value={formData.contactEmail || ''} onChange={e => setFormData({...formData, contactEmail: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Industry / Category</label>
                <select name="businessType" value={formData.businessType || ''} onChange={e => setFormData({...formData, businessType: e.target.value})} className="input-field" required>
                  <option value="">Select Type</option>
                  <option>Restaurant</option>
                  <option>Bar</option>
                  <option>Salon</option>
                  <option>Bowling</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Tax ID / EIN</label>
                <input name="ein" placeholder="IRS EIN (e.g., 12-3456789)" value={formData.ein || ''} onChange={e => setFormData({...formData, ein: e.target.value})} className="input-field" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">POS Merchant ID</label>
                <input name="merchantId" placeholder="Merchant ID" value={formData.merchantId || ''} onChange={e => setFormData({...formData, merchantId: e.target.value})} className="input-field" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xs space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <MapPin size={16} className="text-blue-600" />
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">Physical Business Address & State Jurisdiction</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Street Address</label>
                <input name="addressLine1" placeholder="123 Main Street" value={formData.addressLine1 || ''} onChange={e => setFormData({...formData, addressLine1: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Suite, Floor, Building (Optional)</label>
                <input name="addressLine2" placeholder="Suite 400" value={formData.addressLine2 || ''} onChange={e => setFormData({...formData, addressLine2: e.target.value})} className="input-field" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">City</label>
                <input name="city" placeholder="City" value={formData.city || ''} onChange={e => setFormData({...formData, city: e.target.value})} className="input-field" required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">State / Jurisdiction</label>
                <select name="state" value={formData.state || ''} onChange={e => setFormData({...formData, state: e.target.value})} className="input-field font-medium text-gray-900" required>
                  <option value="">Select State</option>
                  {US_STATES.map(st => (
                    <option key={st.code} value={st.code}>
                      {st.name} ({st.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">ZIP / Postal Code</label>
                <input name="zipCode" placeholder="90210" value={formData.zipCode || ''} onChange={e => setFormData({...formData, zipCode: e.target.value})} className="input-field" required />
              </div>
            </div>
          </div>

          <button type="submit" className="btn-primary w-full py-4 text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-blue-500/20 min-h-[48px]">
            Save Profile Changes
          </button>
        </div>
      )}

      {!tenantId && (
        <div className="pt-2 text-center">
          <button
            type="button"
            onClick={() => navigate('/setup-link')}
            className="inline-flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors p-2"
          >
            <LinkIcon size={14} />
            <span>Joining an existing team? Click here to enter your Invite Code / Tenant ID</span>
          </button>
        </div>
      )}
      
      <button 
        type="button" 
        onClick={() => signOut(auth)} 
        className="w-full text-center text-gray-400 text-xs font-bold uppercase tracking-widest mt-2 hover:text-gray-900 transition-colors p-2"
      >
        Not your account? Sign Out
      </button>

      {status && (
        <div className="flex items-center justify-center gap-2 p-4 bg-green-50 text-green-700 border border-green-100 rounded-2xl animate-in fade-in slide-in-from-top-2 tracking-tighter font-bold uppercase text-[10px]">
          <CheckCircle2 size={16} />
          {status}
        </div>
      )}
    </form>
  );
};

